import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { defaultAvailableStates } from '@shared';
import {
  FormInput,
  FormSelect,
  PrimarySecondaryButtonContainer,
  SubmitButton,
} from '@/components/FormComponents';
import { FormErrorAlert } from '@/components/FormErrorAlert';
import { Card } from '@/components/ui/card';
import { Button } from '@design-system';
import { useCarescoutStateProbe } from '../hooks/use-carescout-state-probe';
import { getErrorMessage } from '../utils/get-error-message';

const schema = z.object({
  quoteId: z.string().min(1, 'Quote ID is required'),
  clientId: z.string().min(1, 'Client ID is required'),
  state: z
    .string()
    .min(2, 'State is required')
    .max(2, 'Use 2-letter USPS code')
    .transform(v => v.toUpperCase()),
});

type FormValues = z.infer<typeof schema>;

function base64ToBlobUrl(pdfBase64: string): string {
  const byteChars = atob(pdfBase64);
  const bytes = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    bytes[i] = byteChars.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: 'application/pdf' });
  return URL.createObjectURL(blob);
}

export function CarescoutStateProbeForm() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialValuesRef = useRef({
    quoteId: searchParams.get('quoteId') ?? '',
    clientId: searchParams.get('clientId') ?? '',
    state: (searchParams.get('state') ?? '').toUpperCase(),
  });

  const stateOptions = useMemo(
    () => defaultAvailableStates.map(s => ({ value: s.value, label: `${s.value} — ${s.label}` })),
    [],
  );

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: initialValuesRef.current,
  });

  const { probe, pdfBase64, isPending, isError, error, reset } = useCarescoutStateProbe();

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [submittedState, setSubmittedState] = useState<string | null>(null);

  useEffect(() => {
    if (!pdfBase64) {
      setPdfUrl(null);
      return;
    }
    const url = base64ToBlobUrl(pdfBase64);
    setPdfUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pdfBase64]);

  const onSubmit = (data: FormValues) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set('quoteId', data.quoteId);
        next.set('clientId', data.clientId);
        next.set('state', data.state);
        return next;
      },
      { replace: true },
    );
    reset();
    setSubmittedState(data.state);
    probe(data);
  };

  const downloadName = submittedState ? `carescout-probe-${submittedState}.pdf` : 'carescout-probe.pdf';

  return (
    <div className="space-y-6">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-4"
      >
        <FormInput
          label="Traditional LTC Quote ID"
          id="quoteId"
          type="text"
          placeholder="Enter traditionalLTCQuoteId"
          error={errors.quoteId}
          register={register('quoteId')}
          required={true}
        />

        <FormInput
          label="Client ID"
          id="clientId"
          type="text"
          placeholder="Enter clientId (advisor + zipcode pulled from this client)"
          error={errors.clientId}
          register={register('clientId')}
          required={true}
        />

        <FormSelect
          label="Applicant State Override"
          id="state"
          name="state"
          control={control}
          error={errors.state}
          required={true}
          options={stateOptions}
          placeholder="Select state to test"
        />

        <PrimarySecondaryButtonContainer>
          <SubmitButton
            label="Run Probe"
            isLoading={isPending}
          />
        </PrimarySecondaryButtonContainer>

        {isError && (
          <FormErrorAlert
            mainErrorMessage={getErrorMessage(error, 'Probe failed')}
            className="mt-4"
          />
        )}
      </form>

      {pdfUrl && (
        <Card className="space-y-4 p-6">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              <strong>Genworth accepted</strong> the quote with state{' '}
              <strong>{submittedState}</strong>. PDF below.
            </div>
            <Button
              asChild
              variant="outline"
              size="sm"
            >
              <a href={pdfUrl} download={downloadName}>Download PDF</a>
            </Button>
          </div>
          <iframe
            src={pdfUrl}
            title="CareScout probe illustration"
            className="h-[700px] w-full rounded border"
          />
        </Card>
      )}
    </div>
  );
}
